import { useState } from "react";
import { Badge, Group, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { Alert, Button, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconArrowsExchange, IconKey, IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { CataloguePermission, CataloguePlan, PlanKind } from "@models/access";
import { AdminTable, col } from "@components/AdminTable";
import { useAccessScope, useOpenPlans, usePermissionCatalogue, useRoles } from "@hooks/access";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { CutoverWizard, EnvironmentBanner, currentStep } from "@features/Access";
import { formatShortDateTime } from "@utils/monitoringFormat";

const YesNo = ({ value, warn }: { value: boolean; warn: boolean }) => (
  <Badge size="sm" variant="light" color={warn ? "orange" : value ? "green" : "gray"}>{value ? "✓" : "✗"}</Badge>
);

/**
 * The application's permission catalogue: what the licence offers, what the
 * role template lists, and which roles hold each key. Adding, renaming and
 * deleting go through the cutover wizard, never one BeeHive call.
 */
export const PermissionsPage = () => {
  const { t } = useTranslation();
  const { canManagePermissionCatalogue } = useAdminPermissions();
  const catalogue = usePermissionCatalogue();
  const roles = useRoles();
  const scope = useAccessScope();
  const plans = useOpenPlans(canManagePermissionCatalogue);
  const [wizard, setWizard] = useState<{ kind: PlanKind; resume: CataloguePlan | null } | null>(null);

  const mismatched = new Set((catalogue.data?.mismatches ?? []).map((m) => m.key));
  const openPlans = plans.data ?? [];
  const reload = () => {
    catalogue.reload();
    plans.reload();
  };

  const columns = [
    col.twoLine<CataloguePermission>("key", t("admin.access.permissions.columns.key"),
                                     (p) => ({ primary: p.name, secondary: p.key }), { mono: true }),
    col.badge<CataloguePermission>("licence", t("admin.access.permissions.columns.inLicence"),
                                   (p) => <YesNo value={p.inLicence} warn={mismatched.has(p.key)} />, 112),
    col.badge<CataloguePermission>("template", t("admin.access.permissions.columns.inRoleTemplate"),
                                   (p) => <YesNo value={p.inRoleTemplate} warn={mismatched.has(p.key)} />, 128),
    col.badge<CataloguePermission>("roles", t("admin.access.permissions.columns.roles"), (p) =>
      p.roles.length === 0 ? <Text size="sm" c="dimmed">-</Text> : (
        <Tooltip label={p.roles.join(", ")} multiline maw={360}>
          <Text size="sm" truncate>{p.roles.join(", ")}</Text>
        </Tooltip>
      ), 240),
  ];

  const open = (kind: PlanKind) => setWizard({ kind, resume: null });

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.access.permissions.title")}
        description={t("admin.access.permissions.description")}
        icon={<IconKey size={28} />}
        actions={canManagePermissionCatalogue && (
          <Group gap="sm">
            <Button leftSection={<IconPlus size={16} />} onClick={() => open("add")} disabled={!catalogue.data}>
              {t("admin.access.permissions.add")}
            </Button>
            <Button variant="light" leftSection={<IconArrowsExchange size={16} />} onClick={() => open("rename")}
                    disabled={!catalogue.data}>
              {t("admin.access.permissions.rename")}
            </Button>
            <Button variant="light" color="red" leftSection={<IconTrash size={16} />} onClick={() => open("delete")}
                    disabled={!catalogue.data}>
              {t("admin.access.permissions.delete")}
            </Button>
          </Group>
        )}
      />
      <EnvironmentBanner />

      {openPlans.map((plan) => (
        <Alert key={plan.id} color="orange" title={t("admin.access.permissions.openPlan", { kind: plan.kind })}>
          <Group justify="space-between">
            <Text size="sm">
              {t("admin.access.permissions.openPlanDetail", {
                step: plan.steps.findIndex((s) => s.n === currentStep(plan)) + 1 || plan.steps.length, total: plan.steps.length, by: plan.createdBy ?? "-",
                at: plan.createdAt ? formatShortDateTime(plan.createdAt) : "-",
              })}
            </Text>
            {canManagePermissionCatalogue && (
              <Button size="xs" onClick={() => setWizard({ kind: plan.kind, resume: plan })}>
                {t("admin.access.permissions.resume")}
              </Button>
            )}
          </Group>
        </Alert>
      ))}

      {catalogue.error && (
        <Alert color={catalogue.forbidden ? "gray" : "red"} title={t("admin.access.permissions.loadFailed")}>
          {catalogue.error}
        </Alert>
      )}
      {mismatched.size > 0 && (
        <Alert color="yellow" title={t("admin.access.permissions.mismatchTitle")}>
          {t("admin.access.permissions.mismatch", { keys: [...mismatched].join(", ") })}
        </Alert>
      )}

      {catalogue.loading && !catalogue.data ? <Skeleton h={240} radius="md" /> : (
        catalogue.data?.modules.map((module) => (
          <SectionCard key={module.name} title={`${module.name} (${module.permissions.length})`}>
            <AdminTable
              columns={columns}
              data={module.permissions}
              getRowKey={(p) => p.key}
              emptyMessage={t("admin.access.permissions.empty")}
            />
          </SectionCard>
        ))
      )}

      {wizard && catalogue.data && (
        <CutoverWizard
          opened
          kind={wizard.kind}
          resume={wizard.resume}
          catalogue={catalogue.data}
          roles={roles.data ?? []}
          environment={scope.data?.application.code ?? scope.data?.environment ?? ""}
          onClose={() => {
            setWizard(null);
            reload();
          }}
          onChanged={reload}
        />
      )}
    </Stack>
  );
};
